import { useRef } from "react";
import { useReducedMotion } from "motion/react";
import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";
import { formatActivationCountForDisplay, formatDuration } from "../../lib/format";
import { useFriendlyAppName } from "../../lib/apps/useFriendlyAppName";
import { ActivationCountText } from "./ActivationCountText";
import { computeRingSegments, resolveRingDenominator } from "./ringGeometry";
import {
  isPrimaryPointerButton,
  shouldHandleRingSegmentPointerUp,
} from "./screen0RingFocus";

export type RingMetric = "time" | "opens";

export type RingSegment = {
  /** Sync label for categories; apps resolve via hook in the hit target. */
  name?: string;
  displayName?: string;
  appIdentifier?: string;
  category?: string;
  isCategory?: boolean;
  segmentKey: string;
  seconds: number;
  opens?: number;
  color: string;
};

type Props = {
  segments: RingSegment[];
  totalSeconds: number;
  totalOpens?: number;
  /** Sum of durationSeconds across ALL entities (not just the displayed top 5);
   * used as the ring denominator so arcs show true share and never overfill. */
  entitiesTotalSeconds?: number;
  /** Sum of opens across ALL entities; ring denominator for the opens metric. */
  entitiesTotalOpens?: number;
  metric?: RingMetric;
  unlockCount?: number;
  unlockUnavailableLabel?: string;
  selectedIndex?: number | null;
  onSegmentClick?: (index: number | null) => void;
  onCenterClick?: () => void;
  /** Replays the clockwise draw animation and dims values while data refreshes. */
  isRefreshing?: boolean;
  /** When true, plays the clockwise draw animation once; when false, segments render fully drawn. */
  animateDraw?: boolean;
  /** Tints displayed data values with average-mode green hierarchy. */
  averageMode?: boolean;
};

function segmentValue(segment: RingSegment, metric: RingMetric): number {
  return metric === "opens" ? (segment.opens ?? 0) : segment.seconds;
}

export function Screen0Ring({
  segments,
  totalSeconds,
  totalOpens,
  entitiesTotalSeconds,
  entitiesTotalOpens,
  metric = "time",
  unlockCount,
  unlockUnavailableLabel,
  selectedIndex = null,
  onSegmentClick,
  onCenterClick,
  isRefreshing = false,
  animateDraw = false,
  averageMode = false,
}: Props) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const prefersReducedMotion = useReducedMotion();
  const radius = 89;
  const strokeWidth = 24;
  const circumference = 2 * Math.PI * radius;
  const isFocused = selectedIndex !== null;
  const resolvedTotalOpens = totalOpens ?? unlockCount ?? segments.reduce((sum, segment) => sum + (segment.opens ?? 0), 0);
  const segmentValues = segments.map((segment) => segmentValue(segment, metric));
  const segmentsSum = segmentValues.reduce((sum, value) => sum + value, 0);
  const reportedTotal = metric === "opens" ? resolvedTotalOpens : totalSeconds;
  const populationTotal = metric === "opens" ? (entitiesTotalOpens ?? 0) : (entitiesTotalSeconds ?? 0);
  const ringTotal = resolveRingDenominator(reportedTotal, populationTotal, segmentsSum);

  const segmentData = computeRingSegments(segmentValues, ringTotal, circumference);

  const dimTransition = prefersReducedMotion
    ? undefined
    : "opacity 200ms cubic-bezier(0.16, 1, 0.3, 1)";
  const refreshOpacity = isRefreshing ? 0.55 : 1;
  const shouldAnimateDraw = animateDraw && !prefersReducedMotion;
  const segmentDrawClass = shouldAnimateDraw
    ? "origin-center animate-[ringDraw_900ms_ease-out_both]"
    : undefined;
  const shellTrackClass = segments.length === 0 ? segmentDrawClass : undefined;
  const shellTrackStyle = segments.length === 0
    ? {
        strokeDasharray: `${circumference} ${circumference}`,
        strokeDashoffset: 0,
      }
    : undefined;

  const selectedSegment = selectedIndex !== null && selectedIndex >= 0 && selectedIndex < segments.length
    ? segments[selectedIndex]!
    : null;
  const centerPrimary = metric === "opens"
    ? (selectedSegment ? (selectedSegment.opens ?? 0) : resolvedTotalOpens)
    : (selectedSegment ? selectedSegment.seconds : totalSeconds);
  const centerSecondaryTime = selectedSegment ? selectedSegment.seconds : totalSeconds;
  const centerSecondaryOpens = selectedSegment
    ? (selectedSegment.opens ?? 0)
    : resolvedTotalOpens;
  const canToggleOpens = resolvedTotalOpens > 0 || segments.some((segment) => (segment.opens ?? 0) > 0);
  const centerInteractive = Boolean(onCenterClick) && (metric === "time" ? canToggleOpens : true);

  const dismissFocus = () => {
    if (isFocused) onSegmentClick?.(null);
  };

  return (
    <div
      className="relative mx-auto aspect-square w-full max-w-[360px]"
      data-today-ring-focus-root={isFocused ? "" : undefined}
      onClick={() => {
        dismissFocus();
      }}
      onPointerUp={(event) => {
        if (!isFocused || !onSegmentClick) return;
        if (event.target !== event.currentTarget) return;
        if (!isPrimaryPointerButton(event.button)) return;
        dismissFocus();
      }}
    >
      <svg
        className="-rotate-90"
        viewBox="0 0 240 240"
        role="group"
        aria-label={metric === "opens" ? t.ring.screenActivationsByApp : t.ring.screenTimeByApp}
      >
        <circle
          key={shouldAnimateDraw ? "track-draw" : "track"}
          cx="120"
          cy="120"
          r={radius}
          fill="none"
          stroke="oklch(24% 0.018 165)"
          strokeWidth={strokeWidth}
          className={shellTrackClass}
          style={{
            ...shellTrackStyle,
            opacity: isFocused ? 0.32 : refreshOpacity,
            transition: dimTransition,
            pointerEvents: "none",
          }}
        />

        {segments.map((segment, i) => {
          const { dash, gapOffset } = segmentData[i]!;
          const isDimmed = isFocused && selectedIndex !== i;
          return (
            <circle
              key={shouldAnimateDraw ? `segment-${i}-draw` : `segment-${i}`}
              cx="120"
              cy="120"
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeLinecap="butt"
              strokeWidth={strokeWidth}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-gapOffset}
              className={segmentDrawClass}
              style={{
                opacity: isDimmed ? 0.18 : refreshOpacity,
                transition: dimTransition,
                pointerEvents: "none",
              }}
            />
          );
        })}

        {isFocused && onSegmentClick ? (
          <RingBackgroundDismissTarget
            radius={radius}
            strokeWidth={strokeWidth}
            onDismiss={dismissFocus}
          />
        ) : null}

        {onSegmentClick &&
          segments.map((segment, i) => {
            const { dash, gapOffset } = segmentData[i]!;
            if (dash <= 0) return null;
            return (
              <RingSegmentHitTarget
                key={segment.segmentKey}
                segment={segment}
                index={i}
                selectedIndex={selectedIndex}
                metric={metric}
                ariaValue={
                  metric === "opens"
                    ? formatTemplate(t.ring.activationsCount, {
                        count: formatActivationCountForDisplay(segmentValue(segment, metric), "integer"),
                      })
                    : formatDuration(segment.seconds, languageCode)
                }
                dash={dash}
                gapOffset={gapOffset}
                circumference={circumference}
                radius={radius}
                onSegmentClick={onSegmentClick}
              />
            );
          })}
      </svg>

      {/* pointer-events-none so taps fall through to SVG; button re-enables center toggle */}
      <div
        className="pointer-events-none absolute inset-0 grid place-items-center text-center"
        style={{ opacity: refreshOpacity, transition: dimTransition }}
      >
        {centerInteractive ? (
          <button
            type="button"
            className="pointer-events-auto grid place-items-center rounded-full bg-transparent px-4 py-3 text-center transition-opacity active:opacity-70"
            style={{ minWidth: 132, minHeight: 132 }}
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.stopPropagation();
              onCenterClick?.();
            }}
            aria-label={metric === "opens" ? t.ring.showScreenTime : t.ring.showScreenActivations}
          >
            <CenterLabel
              metric={metric}
              primaryValue={centerPrimary}
              secondaryTime={centerSecondaryTime}
              secondaryOpens={centerSecondaryOpens}
              unlockUnavailableLabel={unlockUnavailableLabel}
              languageCode={languageCode}
              t={t}
              averageMode={averageMode}
            />
          </button>
        ) : (
          <div className="px-4 py-3">
            <CenterLabel
              metric={metric}
              primaryValue={centerPrimary}
              secondaryTime={centerSecondaryTime}
              secondaryOpens={centerSecondaryOpens}
              unlockUnavailableLabel={unlockUnavailableLabel}
              languageCode={languageCode}
              t={t}
              averageMode={averageMode}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Transparent stroke layer behind segment hit targets; catches ring gaps on Android WebView. */
function RingBackgroundDismissTarget({
  radius,
  strokeWidth,
  onDismiss,
}: {
  radius: number;
  strokeWidth: number;
  onDismiss: () => void;
}) {
  const pointerDownTargetRef = useRef<EventTarget | null>(null);

  return (
    <circle
      cx="120"
      cy="120"
      r={radius}
      fill="none"
      stroke="transparent"
      strokeWidth={strokeWidth + 22}
      aria-hidden
      style={{ pointerEvents: "stroke" }}
      onPointerDown={(event) => {
        event.stopPropagation();
        pointerDownTargetRef.current = event.currentTarget;
      }}
      onPointerUp={(event) => {
        event.stopPropagation();
        if (!isPrimaryPointerButton(event.button)) return;
        if (!shouldHandleRingSegmentPointerUp(pointerDownTargetRef.current, event.currentTarget)) return;
        pointerDownTargetRef.current = null;
        onDismiss();
      }}
      onPointerCancel={() => {
        pointerDownTargetRef.current = null;
      }}
    />
  );
}

function CenterLabel({
  metric,
  primaryValue,
  secondaryTime,
  secondaryOpens,
  unlockUnavailableLabel,
  languageCode,
  t,
  averageMode = false,
}: {
  metric: RingMetric;
  primaryValue: number;
  secondaryTime: number;
  secondaryOpens: number;
  unlockUnavailableLabel?: string;
  languageCode: ReturnType<typeof useScreen0Language>["languageCode"];
  t: ReturnType<typeof getTranslations>;
  averageMode?: boolean;
}) {
  const primaryTimeClass = averageMode ? "today-data-value-primary" : "";
  const secondaryClass = averageMode ? "today-data-value-secondary" : "text-[oklch(72%_0.025_155)]";
  const activationDisplayMode = averageMode ? "averaged" : "integer";
  const formattedPrimaryTime = formatDuration(
    averageMode ? Math.round(primaryValue) : primaryValue,
    languageCode,
  );
  const formattedSecondaryTime = formatDuration(
    averageMode ? Math.round(secondaryTime) : secondaryTime,
    languageCode,
  );

  if (metric === "opens") {
    return (
      <div className="w-full max-w-[148px]">
        <ActivationCountText
          count={primaryValue}
          variant="ring"
          displayMode={activationDisplayMode}
          averageMode={averageMode}
        />
        <div className={`mt-2 text-sm font-medium ${secondaryClass}`}>{formattedSecondaryTime}</div>
      </div>
    );
  }

  return (
    <div>
      <div className={`mono text-5xl font-semibold tracking-normal${primaryTimeClass ? ` ${primaryTimeClass}` : ""}`}>
        {formattedPrimaryTime}
      </div>
      {secondaryOpens > 0 ? (
        <div className={`mt-2 text-sm font-medium ${secondaryClass}`}>
          {formatTemplate(t.ring.activationsCount, {
            count: formatActivationCountForDisplay(secondaryOpens, activationDisplayMode),
          })}
        </div>
      ) : unlockUnavailableLabel ? (
        <div className="mx-auto mt-2 max-w-48 text-xs font-medium leading-5 text-[oklch(64%_0.02_155)]">{unlockUnavailableLabel}</div>
      ) : null}
    </div>
  );
}

function RingSegmentHitTarget({
  segment,
  index,
  selectedIndex,
  metric,
  ariaValue,
  dash,
  gapOffset,
  circumference,
  radius,
  onSegmentClick,
}: {
  segment: RingSegment;
  index: number;
  selectedIndex: number | null;
  metric: RingMetric;
  ariaValue: string;
  dash: number;
  gapOffset: number;
  circumference: number;
  radius: number;
  onSegmentClick: (index: number | null) => void;
}) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const appStoreName = useFriendlyAppName({
    displayName: segment.displayName,
    appIdentifier: segment.appIdentifier,
    category: segment.category,
  });
  const resolvedName = segment.isCategory
    ? (segment.name ?? segment.displayName ?? t.common.unknown)
    : appStoreName;
  const isSelected = selectedIndex === index;
  const strokeWidth = 24;
  const pointerDownTargetRef = useRef<EventTarget | null>(null);
  const handledViaPointerRef = useRef(false);

  const activateSegment = () => {
    onSegmentClick(isSelected ? null : index);
  };

  return (
    <circle
      cx="120"
      cy="120"
      r={radius}
      fill="none"
      stroke="rgba(0,0,0,0.01)"
      strokeWidth={strokeWidth + 22}
      strokeDasharray={`${dash} ${circumference - dash}`}
      strokeDashoffset={-gapOffset}
      data-ring-segment-hit=""
      className="outline-none focus:outline-none focus-visible:outline-none"
      style={{
        pointerEvents: "stroke",
        cursor: "pointer",
        outline: "none",
        WebkitTapHighlightColor: "transparent",
      }}
      onPointerDown={(event) => {
        event.stopPropagation();
        pointerDownTargetRef.current = event.currentTarget;
      }}
      onPointerUp={(event) => {
        event.stopPropagation();
        if (!isPrimaryPointerButton(event.button)) return;
        if (!shouldHandleRingSegmentPointerUp(pointerDownTargetRef.current, event.currentTarget)) return;
        pointerDownTargetRef.current = null;
        handledViaPointerRef.current = true;
        activateSegment();
        event.currentTarget.blur();
      }}
      onPointerCancel={() => {
        pointerDownTargetRef.current = null;
      }}
      onClick={(event) => {
        event.stopPropagation();
        if (handledViaPointerRef.current) {
          handledViaPointerRef.current = false;
          event.preventDefault();
          return;
        }
        activateSegment();
        event.currentTarget.blur();
      }}
      role="button"
      tabIndex={0}
      aria-label={`${resolvedName}: ${ariaValue}`}
      aria-pressed={isSelected}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          activateSegment();
          event.currentTarget.blur();
        }
      }}
    />
  );
}
